import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { Shield, Code2, Lock, Quote } from 'lucide-react';

const techStack = [
  { name: 'React', color: '#61DAFB' },
  { name: 'TypeScript', color: '#3178C6' },
  { name: 'PHP', color: '#777BB4' },
  { name: 'MySQL', color: '#4479A1' },
  { name: 'Tailwind', color: '#06B6D4' },
  { name: 'Slim 4', color: '#719e40' },
  { name: 'OpenRouter AI', color: '#412991' },
  { name: 'Hostinger', color: '#673DE6' },
];

const testimonials = [
  {
    quote: 'Bewhy tidak hanya membangun website kami—mereka membantu kami menemukan cerita yang selama ini belum kami ceritakan.',
    name: 'Rina Maharani',
    role: 'CEO, Kopi Nusantara',
  },
  {
    quote: 'Pendekatan Why-first mereka benar-benar mengubah cara kami melihat kehadiran digital kami. Bukan soal fitur, tapi dampak.',
    name: 'Andi Pratama',
    role: 'CTO, TechVenture ID',
  },
  {
    quote: 'Hasilnya bicara sendiri. Traffic naik 3x, tapi yang lebih penting—kualitas lead yang masuk jauh lebih baik.',
    name: 'Maya Putri',
    role: 'Marketing Director, Batik Aura',
  },
];

export default function Trust() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      data-testid="trust-section"
      ref={ref}
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <motion.span
          className="font-mono text-xs tracking-[0.2em] uppercase block mb-4"
          style={{ color: 'var(--accent-primary)' }}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
        >
          Trust & Integrity
        </motion.span>
        <motion.h2
          className="font-heading text-3xl md:text-5xl font-semibold tracking-tight max-w-3xl mb-16"
          style={{ color: 'var(--text-primary)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
        >
          100% Teknologi Legal & Aman
        </motion.h2>

        {/* Trust Badges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            { icon: Shield, title: 'Lisensi Legal', desc: 'Semua teknologi yang kami gunakan berlisensi resmi. Tidak ada bajakan, tidak ada risiko hukum.' },
            { icon: Code2, title: 'Kode Bersih', desc: 'Best practices, code review, dan dokumentasi lengkap. Kode yang bisa di-maintain jangka panjang.' },
            { icon: Lock, title: 'Data Aman', desc: 'Enkripsi end-to-end, HTTPS, dan compliance terhadap standar keamanan data modern.' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                className="glass-card p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + i * 0.1 }}
                data-testid={`trust-badge-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: theme === 'dark' ? 'rgba(0,255,133,0.08)' : 'rgba(13,148,136,0.08)', color: 'var(--accent-primary)' }}
                >
                  <Icon size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{item.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Tech Stack */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
        >
          <h3 className="font-mono text-xs tracking-[0.2em] uppercase text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
            Tech Stack Kami
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {techStack.map((tech, i) => (
              <div
                key={i}
                className="px-5 py-2.5 rounded-full font-mono text-xs tracking-wider transition-all hover:-translate-y-0.5"
                style={{
                  border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  color: 'var(--text-primary)',
                  background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                }}
                data-testid={`tech-badge-${tech.name.toLowerCase()}`}
              >
                {tech.name}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              className="glass-card p-8"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + i * 0.15 }}
              data-testid={`testimonial-${i}`}
            >
              <Quote size={20} style={{ color: 'var(--accent-primary)', marginBottom: 16 }} />
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--text-primary)' }}>
                "{t.quote}"
              </p>
              <div>
                <p className="font-heading text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t.name}</p>
                <p className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{t.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
